import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { financeApi } from '../../../api/finance';
import { DataTable } from '../../../components/DataTable';
import { Project } from '../../../types/projects';

function money(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * The real, internal financial-control view - genuinely separate
 * from the "Budget" tab (which is the donor-facing proposal budget
 * structure with no link to the Chart of Accounts). Actual and
 * Committed here come from real posted journal lines and real
 * outstanding payables - never estimated. Committed is specifically
 * defined as the outstanding balance of approved-but-unpaid Expense
 * Claims tied to this project, since no Procurement/PO system exists
 * to source a more traditional commitment from.
 */
export function ProjectFinanceTab() {
  const { project } = useOutletContext<{ project: Project }>();
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears() });
  const [fiscalYearId, setFiscalYearId] = useState<number | null>(null);

  const summaryQuery = useQuery({
    queryKey: ['project-finance-summary', project.id, fiscalYearId],
    queryFn: () => financeApi.getProjectFinanceSummary(project.id, fiscalYearId!),
    enabled: !!fiscalYearId,
  });

  const summary = summaryQuery.data?.data;
  const totals = summary?.lines.reduce(
    (acc, l) => ({ budgeted: acc.budgeted + l.budgeted, actual: acc.actual + l.actual, committed: acc.committed + l.committed, available: acc.available + l.available, variance: acc.variance + l.variance }),
    { budgeted: 0, actual: 0, committed: 0, available: 0, variance: 0 }
  );

  return (
    <>
      <section className="card">
        <h2>Finance</h2>
        <p className="muted">Real Budget vs Actual, tied to the Chart of Accounts and posted journal entries - separate from the proposal budget structure in the Budget tab.</p>
        <label>
          Fiscal Year
          <select onChange={(e) => setFiscalYearId(Number(e.target.value) || null)}>
            <option value="">- select -</option>
            {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
          </select>
        </label>
      </section>

      {summary && !summary.hasBudget && (
        <section className="card"><p className="muted">No budget has been created for this project in the selected fiscal year yet. Create one under Finance → Budgets.</p></section>
      )}

      {summary && summary.hasBudget && (
        <section className="card">
          <h3>Budget vs Actual - {project.currency_code}</h3>
          <DataTable
            rows={summary.lines}
            rowKey={(l) => l.accountId}
            emptyMessage="No budget lines."
            columns={[
              { header: 'Account', render: (l) => `${l.accountCode} - ${l.accountName}` },
              { header: 'Cost Centre', render: (l) => l.costCentreName ?? '-' },
              { header: 'Budget', render: (l) => money(l.budgeted) },
              { header: 'Actual', render: (l) => money(l.actual) },
              { header: 'Committed', render: (l) => money(l.committed) },
              { header: 'Available', render: (l) => money(l.available) },
              { header: 'Variance', render: (l) => <span style={{ color: l.variance < 0 ? 'red' : undefined }}>{money(l.variance)}</span> },
            ]}
          />
          {totals && (
            <p style={{ marginTop: '1em' }}>
              <strong>Total: Budget {money(totals.budgeted)} · Actual {money(totals.actual)} · Committed {money(totals.committed)} · Available {money(totals.available)} · Variance {money(totals.variance)}</strong>
            </p>
          )}
        </section>
      )}
    </>
  );
}
